import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink, Paperclip, X } from "lucide-react";
import { api } from "@/lib/api";
import CelebrationModal from "@/components/CelebrationModal";
import Mascot from "@/components/Mascot";

export default function EnglishQuest({ onCompleted }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [quest, setQuest] = useState(null);
  const [answers, setAnswers] = useState({}); // taskId -> {picked, correct}
  const [wrongShake, setWrongShake] = useState({}); // taskId -> true
  const [submitting, setSubmitting] = useState(false);
  const [celebration, setCelebration] = useState(null);

  useEffect(() => { api.get(`/english-quests/${id}`).then((r) => setQuest(r.data)); }, [id]);

  const tasks = useMemo(() => quest?.tasks || [], [quest]);
  const allAnswered = tasks.length === 0 || tasks.every((t) => answers[t.id] !== undefined);

  function pick(task, idx) {
    // Lock on first pick — correct or wrong, no retries.
    if (answers[task.id] !== undefined) return;
    const isCorrect = idx === task.correct_index;
    setAnswers((prev) => ({ ...prev, [task.id]: { picked: idx, correct: isCorrect } }));
    if (!isCorrect) {
      setWrongShake((s) => ({ ...s, [task.id]: true }));
      setTimeout(() => setWrongShake((s) => ({ ...s, [task.id]: false })), 500);
    }
  }

  async function markDone() {
    if (!quest || quest.completed_at || !allAnswered) return;
    setSubmitting(true);
    const r = await api.post(`/english-quests/${id}/complete`);
    setSubmitting(false);
    const xpItem = (r.data.new_items || []).find((x) => x.kind === "xp" && x.label === "Quest complete");
    setCelebration({ title: "Quest complete!", subtitle: xpItem ? `+${xpItem.amount} XP` : "Great reading!" });
    onCompleted?.(r.data.new_items || []);
  }

  if (!quest) return <div className="pt-10 text-center text-[hsl(var(--ink-soft))]">Loading…</div>;

  const totalTasks = tasks.length;
  const correctCount = tasks.filter((t) => answers[t.id]?.correct).length;

  return (
    <div className="pb-32 pt-5">
      <div className="flex items-center justify-between">
        <Link to="/" className="btn btn-ghost !py-2 !px-4 text-sm" data-testid="back-home">
          <ArrowLeft className="w-4 h-4"/> Back
        </Link>
        <span className="chip chip-amber">+20 XP</span>
      </div>

      {/* Studying mascot banner */}
      <div className="mt-5 surface-amber px-5 py-5 flex items-center gap-4">
        <Mascot state="studying" size={110} testId="quest-mascot"/>
        <div className="min-w-0">
          <p className="micro-label text-[hsl(var(--amber-deep))]">English Quest</p>
          <h1 className="text-[26px] text-[hsl(var(--ink))] leading-tight" data-testid="quest-title">{quest.title}</h1>
          {totalTasks > 0 && (
            <p className="text-sm text-[hsl(var(--ink-soft))] mt-1" data-testid="task-progress">
              {correctCount} / {totalTasks} tasks correct
            </p>
          )}
        </div>
      </div>

      {/* Reading passage */}
      {(quest.instructions || quest.content || quest.attachment_url || quest.external_url) && (
        <div className="mt-5 surface px-5 py-5 space-y-3">
          {quest.instructions && (
            <p className="text-[hsl(var(--ink))] text-[16px] leading-relaxed" data-testid="quest-instructions">
              {quest.instructions}
            </p>
          )}
          {quest.content && (
            <div className="p-4 rounded-2xl bg-[hsl(var(--surface-2))] text-[hsl(var(--ink))] whitespace-pre-wrap" data-testid="quest-content">
              {quest.content}
            </div>
          )}
          <div className="flex gap-4">
            {quest.attachment_url && (
              <a href={quest.attachment_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[hsl(var(--violet-deep))] font-semibold">
                <Paperclip className="w-4 h-4"/> Attachment
              </a>
            )}
            {quest.external_url && (
              <a href={quest.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[hsl(var(--violet-deep))] font-semibold">
                <ExternalLink className="w-4 h-4"/> Open link
              </a>
            )}
          </div>
        </div>
      )}

      {/* Tasks (multiple choice) */}
      {tasks.length > 0 && (
        <div className="mt-5 space-y-4" data-testid="tasks-list">
          <h2 className="text-[20px] text-[hsl(var(--ink))]">Now your turn</h2>
          {tasks.map((task, i) => {
            const a = answers[task.id];
            const locked = a !== undefined;
            return (
              <div
                key={task.id}
                data-testid={`task-${i}`}
                data-correct={a?.correct ? "true" : "false"}
                data-answered={locked ? "true" : "false"}
                className={`surface p-5 transition ${wrongShake[task.id] ? "animate-jig" : ""}`}
              >
                <p className="micro-label mb-1">Question {i + 1}</p>
                <p className="text-[17px] font-semibold text-[hsl(var(--ink))]">{task.question}</p>
                <div className="mt-3 grid gap-2">
                  {task.options.map((opt, idx) => {
                    const picked = a?.picked === idx;
                    const wasCorrect = picked && a.correct;
                    const wasWrong = picked && !a.correct;
                    return (
                      <button
                        key={idx}
                        onClick={() => pick(task, idx)}
                        disabled={locked}
                        data-testid={`task-${i}-option-${idx}`}
                        className={`text-left px-4 py-3 rounded-2xl font-semibold border-2 transition
                          ${wasCorrect
                            ? "bg-[hsl(var(--violet-wash))] border-[hsl(var(--violet))] text-[hsl(var(--violet-deep))]"
                            : wasWrong
                            ? "bg-[hsl(0_80%_96%)] border-[hsl(0_70%_72%)] text-[hsl(0_60%_40%)]"
                            : locked
                            ? "bg-[hsl(var(--surface-2))] border-transparent text-[hsl(var(--ink-mute))] cursor-not-allowed"
                            : "bg-white border-transparent text-[hsl(var(--ink))] hover:border-[hsl(var(--violet-wash))] shadow-[0_4px_12px_-8px_hsla(258,20%,20%,0.3)]"}
                        `}
                      >
                        <span className="inline-flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                            ${wasCorrect ? "bg-[hsl(var(--violet))] text-white"
                              : wasWrong ? "bg-[hsl(0_70%_60%)] text-white"
                              : "bg-[hsl(var(--surface-2))] text-[hsl(var(--ink-soft))]"}`}>
                            {wasCorrect ? <Check className="w-3.5 h-3.5" strokeWidth={3}/> : wasWrong ? <X className="w-3.5 h-3.5" strokeWidth={3}/> : String.fromCharCode(65 + idx)}
                          </span>
                          <span>{opt}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {a && !a.correct && (
                  <p className="mt-2 text-sm font-semibold text-[hsl(0_60%_45%)]">
                    Not quite. The correct answer was <span className="font-bold">{String.fromCharCode(65 + task.correct_index)}</span>.
                  </p>
                )}
                {a?.correct && (
                  <p className="mt-2 text-sm font-semibold text-[hsl(var(--violet-deep))]">Correct!</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Finish bar */}
      <div className="fixed inset-x-0 bottom-20 px-5 pointer-events-none">
        <div className="max-w-md mx-auto pointer-events-auto">
          {quest.completed_at ? (
            <div className="surface-soft p-4 flex items-center gap-3" data-testid="quest-already-done">
              <div className="w-10 h-10 rounded-full bg-[hsl(var(--violet-wash))] flex items-center justify-center">
                <Check className="w-5 h-5 text-[hsl(var(--violet-deep))]" strokeWidth={2.8}/>
              </div>
              <div>
                <p className="font-semibold text-[hsl(var(--ink))]">Already finished!</p>
                <p className="text-sm text-[hsl(var(--ink-soft))]">Great job on this one.</p>
              </div>
            </div>
          ) : (
            <button
              onClick={markDone}
              disabled={submitting || !allAnswered}
              data-testid="mark-english-finished-btn"
              className="btn btn-primary w-full text-base disabled:opacity-50"
            >
              {submitting
                ? "Saving…"
                : totalTasks === 0
                  ? "Mark as finished"
                  : allAnswered
                    ? "Mark as finished"
                    : `Answer all ${totalTasks} tasks`}
            </button>
          )}
        </div>
      </div>

      <CelebrationModal
        open={!!celebration}
        onClose={() => { setCelebration(null); nav("/"); }}
        title={celebration?.title}
        subtitle={celebration?.subtitle}
        mascotState="celebrating"
        cta="Back home"
        testid="english-celebration"
      />
    </div>
  );
}
